import { Species } from './species';
import { Disease } from './disease';
import { SurgicalOperation } from './surgicalOperation';
import { OrganizationFosterFamily, Organization, AnimalRefugeCategory } from './organization';
import { VaccineLabel, VaccineValence, VaccineConfiguration } from './vaccine';
import { FlowControl } from './flowControl';
import { User } from './user';
import { DateUtilsService } from '../services/date-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { blobToBase64 } from '../utils'
import { AgendaService } from '../services/agenda.service';
import { AgendaEvent } from './agenda';
import { Competition } from './competition';
import { LogBookPost } from './logBook';
import { LogbookService } from '../services/logbook.service';

export class AnimalVaccine {
    id: number | null = null;
    label: VaccineLabel | undefined = undefined;
    isIgnorePreviousError: boolean = false;

    file: string | null = null
    fileNewName: string | null = null;
    fileBase64: string | null = null

    date: string = '';
    dateValidStart: string | null = null;
    dateEnd: string | null = null;
    configuration: VaccineConfiguration | null = null
    isError: boolean = false;
    errorCode: string = '';
    lateDays: number = 0;

    loadFileBase64(http: HttpClient) {
        return new Promise((resolve, reject) => {
            if(!this.file || this.fileBase64) {
                resolve(this);
            } else {
                http.get(this.file ?? '',  { responseType: 'blob' as 'blob' }).subscribe(data => {
                    blobToBase64(data).then((res: any) =>  {
                        this.fileBase64 = res;
                        resolve(this);
                    });
                },
                () => {
                    resolve(this);
                });
            }
        });
    }
}

export class AnimalVaccineValence {
    valence: VaccineValence | undefined = undefined;
    vaccines: AnimalVaccine[] = [];
    vaccinesIgnored: AnimalVaccine[] = [];
    isWarning: boolean = false;
    isIgnoreLatestError: boolean = false;
}

export class AnimalDocument {
    id: number | null = null;
    file: string | null = null;
    fileName: string | null = null;
    base64: string | null = null;
    name: string | null = null;
}

export class AnimalTag {
    id: number | null = null;
    date: string = '';
    tag: string = ''
}

export class AnimalMapPlanItem {
    id: number | null = null;
    name: string = '';
    polyPoints: string = ''
}

export class AnimalTemporaryAdoption {
    id: number | null = null;
    dateStart: string | null = null;
    dateEnd: string | null = null;
    firstName: string | null = null;
    lastName: string | null = null;
    email: string | null = null;
    addr: string | null = null;
    phoneNumber: string | null = null;
    userId: number| null = null;
    
    isActive() {
        return this.dateEnd == null
    }
}

export class Animal {
    id: number | null = null;
    userId: number| null = null;
    userFirstName: string| null = null;
    userLastName: string| null = null;
    userIsRefuge: boolean = false;
    isShareWithMe: boolean = false;
    isShareIsWrite: boolean = false;
    hasEditRights: boolean = false;
    
    description: string ='';
    geneticTests: string = '';
    nameOfficial: string = '';
    name: string = '';
    affix: string = '';
    species: Species | undefined = undefined;
    isDead: boolean = false;
    dateDeath: string | null = null;

    chip: string = '';
    race: string = '';
    birthday: string = '';
    isHidden: boolean = false;
    passportCode: string = '';
    isLOF: boolean = false;
    isConfirmed: boolean = false;
    gender: string = '';
    category: string = '99';
    displayOnPublicPage: boolean = false;

    avatarNewFileName: string | null = null;
    avatar: string = '';
    avatarBase64: string | null = null;

    valences: AnimalVaccineValence[] = [];
    diseases: AnimalDisease[] = [];
    surgicalOperations: AnimalSurgicalOperation[] = [];
    weights: AnimalWeight[] = [];

    documents: AnimalDocument[] = [];

    tags: AnimalTag[] = [];
    competitions: Competition[] | null = null;

    mapPlanItem: AnimalMapPlanItem | null = null
    fosterFamily: OrganizationFosterFamily | null = null
    
    specificFood: boolean = false;
    descriptionFood: string | null = null;

    refugeHistory: string | null = null;
    refugeCharacter: string | null = null;
    refugeAgreements: string | null = null;
    refugeAdoptingProfile: string | null = null;
    refugeAdoptionAvailable: boolean = true;
    refugeVisibleOnPublicPage: boolean = true;
    refugeBitingIs: boolean = false;
    refugeMuzzleIs: boolean = false;
    refugeCategory: AnimalRefugeCategory | null = null;
    temporaryAdoptions: AnimalTemporaryAdoption[] = [];

    refugeCollarIs: boolean = false;
    refugeHarnessIs: boolean = false;
    refugeDogOkLevel: number = 1;
    refugeCatOkLevel: number = 1;
    refugeWomanOkLevel: number = 1;
    refugeManOkLevel: number = 1;
    refugeKidsOkLevel: number = 1;
    refugeCarOkLevel: number = 1;
    refugeBikeOkLevel: number = 1;
    refugeRunnerOkLevel: number = 1;
    refugeHealth: string | null = null;
    refugeEnclosureOKIs: boolean = false;
    refugeWalkingInfo: string | null = null;
    organizationDateEntry: string | null = null;


    //Pedigree
    father: Animal | null = null;
    mother: Animal | null = null;
    //Spécifique aux animaux qui sont uniquement utilisée dans un pedigree
    pedigreeAnimalIs: boolean = false;
    // Pour le pedigre on utilise un user spécifique pour le propriétaire
    // le champ "user" est toujours null afin que l'animal ne remonte pas dans toutes les autres requêtes
    pedigreeUserId: number | null = null;
    pedigreeOwnerName: string | null = null;

    //Eleveur
    breederReserveIs: boolean = false;

    getName() {
        if(this.name != null && this.name != '')
            return this.name;
        else
            return this.nameOfficial;
    }

    getLogBookComments(logBookService: LogbookService): LogBookPost[] {
        return logBookService.logBookPosts.filter(a => a.animals.map(a => a.id).includes(this.id ?? 0));
    }

    getCurrentTemporaryAdoption(): AnimalTemporaryAdoption | null  {
        if(this.temporaryAdoptions.length > 0 && this.temporaryAdoptions[this.temporaryAdoptions.length - 1].isActive())
            return this.temporaryAdoptions[this.temporaryAdoptions.length - 1]
        return null;
    }

    getLatestWeight(): AnimalWeight | null {
        if (this.weights.length == 0) {
            return null;
        }
        return this.weights[0];
    }

    getGenderLabel(translate: TranslateService) {
        if(this.gender == 'm')
            return translate.instant('Male')
        else
            return translate.instant('Female')
    }

    getAge(dateUtils: DateUtilsService, date: Date | null = null): AnimalAge {
        var today = new Date()
        if(date  != null)
            today = date
        var birthday = dateUtils.fromString(this.birthday) ?? new Date()
        let diffYears = today.getFullYear() - birthday.getFullYear();
        let diffMonths = today.getMonth() - birthday.getMonth();
      
        if (diffMonths < 0) {
          diffYears--;
          diffMonths += 12;
        }
      
        if (today.getDate() < birthday.getDate()) {
          diffMonths--;
          if (diffMonths < 0) {
            diffYears--;
            diffMonths += 12;
          }
        }

        const differenceEnMs: number = Math.abs(today.getTime() - birthday.getTime());
        const differenceEnJours = Math.floor(Math.max(1, differenceEnMs / (1000 * 60 * 60 * 24)));
        const differenceEnSemaines = differenceEnJours / 7;

        return {
          years: diffYears,
          months: diffMonths,
          days: differenceEnJours,
          weeks: differenceEnSemaines,
        };
    }

    getAgeLabel(dateUtils: DateUtilsService, translate: TranslateService, date: Date | null = null): string {
        var age = this.getAge(dateUtils, date)
        var label: string = ''
        if(age.years > 0)
            label = label + String(age.years)+ ' ' + translate.instant('years') + ' '
        if(age.months > 0)
            label = label + String(age.months)+ ' ' + translate.instant('months')
        if(age.years == 0 && age.months == 0)
            label = label + String(age.days) + ' ' + translate.instant('days')
        return label;
    }

    getVaccineValencesDelayed(dateUtils: DateUtilsService, date: Date): { [key: string]: any }[] {
        var ret: { [key: string]: any }[] = [];
        for(var animalVaccineValence of this.valences) {
            var i: number = 0;
            var done: boolean = false;
            while(!done && i < animalVaccineValence.vaccines.length) {
                var vaccine: AnimalVaccine = animalVaccineValence.vaccines[i];
                if(vaccine.isError && vaccine.errorCode == 'noVaccine' && vaccine.isIgnorePreviousError) {
                    done = true
                } else if(vaccine.isError && vaccine.errorCode == 'noVaccine') {
                    i+=1
                    continue;
                }

                if(vaccine.isError || (dateUtils.fromString(vaccine?.dateEnd) ?? new Date()) < date) {
                    ret.push({'valence': animalVaccineValence, 'vaccine': vaccine})
                    done = true
                }

                if((dateUtils.fromString(vaccine?.dateEnd) ?? new Date()) >= date) {
                    done = true;
                }

                i+=1
            }
        }
        return ret;
    }

    getDiseaseNotEnd() {
        return this.diseases.filter(a => a.isEnd == false)
    }

    getDiseaseEnd() {
        return this.diseases.filter(a => a.isEnd == true)
    }

    getWalking(dateUtils: DateUtilsService, organization: Organization | null | undefined, date: Date, hoursStart: number, hoursEnd: number): {'start': FlowControl, 'end': FlowControl | null, 'duration': string | null } | null {
        let flows: FlowControl[] = this.getFlowControls(organization).filter(a => a.action == 'startWalk' || a.action == 'endWalk')
        if(flows.length == 0)
            return null;

        let endFlow: FlowControl | null = null;

        for(let flow of flows) {
            let flowDate = dateUtils.fromString(flow.date ?? null)
            if(!flowDate)
                continue;

            if(flow.action == 'endWalk')
                endFlow = flow;
            else if(flowDate.getFullYear() == date.getFullYear() && flowDate.getMonth() == date.getMonth() && flowDate.getDate() == date.getDate() && flowDate.getHours() >= hoursStart && flowDate.getHours() <= hoursEnd) {
                let duration: string | null = null
                if(endFlow) {
                    const ms = (dateUtils.fromString(endFlow.date)?.getTime() ?? 0) - (dateUtils.fromString(flow.date)?.getTime() ?? 0);
                    const hours = ms / (1000 * 60 * 60);
                    const minutes = (ms % (1000 * 60 * 60)) / (1000 * 60);
                    duration = Math.floor(hours).toString().padStart(2, '0') + ':' + Math.floor(minutes).toString().padStart(2, '0');
                }
                return {'start': flow, 'end': endFlow, duration: duration}
            }
        }

        return null;
    }

    getLatestMeal(organization: Organization | null | undefined): FlowControl | null {
        if(this.getFlowControls(organization).filter(a => a.action == 'meal').length == 0)
            return null;

        return this.getFlowControls(organization).filter(a => a.action == 'meal')[0]
    }

    getLatestMealAge(dateUtils: DateUtilsService, organization: Organization | null | undefined): number | null {
        if(this.getFlowControls(organization).filter(a => a.action == 'meal').length == 0)
            return null;

        let date = dateUtils.fromString(this.getFlowControls(organization).filter(a => a.action == 'meal')[0]?.date ?? null)
        if(!date)
            return null

        var today = new Date()
        const date1Ms = date.getTime();
        const date2Ms = today.getTime();

        const differenceMs = Math.abs(date1Ms - date2Ms);
        const differenceHeures = differenceMs / (1000 * 60 * 60);
        return Math.floor(differenceHeures);
    }

    getFlowControls(organization: Organization | null | undefined) {
        return organization?.flowControls.filter(a => a.animalId == this.id).sort((a: FlowControl, b: FlowControl) => b.date?.localeCompare(a.date ?? '') ?? 0) ?? []
    }

    getLatestWalck(organization: Organization | null | undefined): FlowControl | null {
        if(this.getFlowControls(organization).filter(a => a.action == 'startWalk').length == 0)
            return null;

        return this.getFlowControls(organization).filter(a => a.action == 'startWalk')[0]
    }

    getLatestWalckAge(dateUtils: DateUtilsService, organization: Organization | null | undefined): number | null {
        if(this.getFlowControls(organization).filter(a => a.action == 'startWalk').length == 0)
            return null;

        let date = dateUtils.fromString(this.getFlowControls(organization).filter(a => a.action == 'startWalk')[0]?.date ?? null)
        if(!date)
            return null

        var today = new Date()
        const date1Ms = date.getTime();
        const date2Ms = today.getTime();

        const differenceMs = Math.abs(date1Ms - date2Ms);
        const differenceHeures = differenceMs / (1000 * 60 * 60);
        return Math.floor(differenceHeures);
    }


    getChipColorClass<K extends keyof Animal>(fieldName: K) {
        if(this[fieldName] === true)
            return 'danger'
        if(this[fieldName] === false)
            return ''
        if(this[fieldName] === 1)
            return 'success'
        else if(this[fieldName] === 2)
            return 'warning'
        else if(this[fieldName] === 3)
            return 'danger'
        return 'danger'
    }

    getChipColorHex<K extends keyof Animal>(fieldName: K) {
        if(this[fieldName] === true)
            return '#FF0000'
        if(this[fieldName] === false)
            return '#008000'
        if(this[fieldName] === 1)
            return '#008000'
        else if(this[fieldName] === 2)
            return '#FFA500'
        else if(this[fieldName] === 3)
            return '#FF0000'
        return '#FF0000'
    }

    getTreatmentsNotClosed(agendaService: AgendaService, dateUtils: DateUtilsService): AgendaEvent[] {
        return agendaService.events.filter(a => a.animals.map(a => a.id).includes(this.id ?? 0) && a.statut?.id == 'validated' && a.type?.isTreatment && a.dateTimeStartIsPast(dateUtils))
    }

}

export class AnimalAge {
    years: number = 0;
    months: number = 0;
    days: number = 0;
    weeks: number = 0;
}

export class AnimalDisease {
    id: number | null = null;
    disease: Disease | null = null;
    date: string | null = null;
    dateEnd: string | null = null;
    isEnd: boolean = false;
}

export class AnimalSurgicalOperation {
    id: number | null = null;
    surgicalOperation: SurgicalOperation | null = null;
    date: string | null = null;
}

export class AnimalWeight {
    id: number | null = null;
    date: string | null = null;
    weight: number = 0;
}


export class AnimalShareUser {
    id: number | null = null;
    firstName: string | null = null;
    lastName: string | null = null;
    email: string | null = null;
}

export class AnimalShareAnimal {
    id: number | null = null;
    name: string | null = null;
}

export class AnimalShare {
    id: number | null = null;
    user: AnimalShareUser | null = null;
    userShare: AnimalShareUser | null = null;
    date: string | null = null;
    isShareAllAnimals: boolean = false;
    isSharePharmacy: boolean = false;
    isWrite: boolean = false;
    animals: AnimalShareAnimal[] = [];
    userShareNotifAgendaIs: boolean = false;
    userShareNotifAnimalIs: boolean = false;
}

